import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, Link, useSearchParams } from 'react-router-dom';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Inbox, Info, RefreshCw, MailWarning, Database, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import PageHeader from '../components/PageHeader';
import usePageTitle from '../hooks/usePageTitle';
import useChatsSummary from '../hooks/useChatsSummary';
import useChatsOwner from '../hooks/useChatsOwner';
import { Button } from '../components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import ThreadList from '../components/inbox/ThreadList';
import ThreadView, { NoThreadPicked } from '../components/inbox/ThreadView';
import PhonePane from '../components/inbox/PhonePane';
import LiveToggle from '../components/inbox/LiveToggle';
import SlotFilter from '../components/inbox/SlotFilter';
import { useChatLive } from '../lib/chatLive';
import { getShowPhone, setShowPhone as rememberShowPhone } from '../lib/chatPhone';
import { r16pUiEnabled } from '../lib/r14Switch';
import { useOptionalAuth } from '../contexts/AuthContext';
import { slotsApi } from '../lib/api';
import { chatsKeys, keepPreviousForOwner } from '../lib/chatsKeys';
import { chatsApi } from '../lib/chatsApi';
import { useChatDraft, getChatDraft, setChatDraft } from '../lib/chatDraft';
import { reserveFab, releaseFab } from '../lib/fabStack';
import { decodeSlots, slotsParamFrom, togglePick, pickParams, SLOTS_PARAM_MAX } from '../lib/slotPick';
import {
  normaliseInfo, normaliseThreadList, normaliseThreadDetail, classifyChatsError, chatsErrorText, chatLog, threadPollMs,
  basisSeqOf, newClientRequestId, slotParamFrom, threadParamFrom, filterFrom, FILTERS, DETACHED_FILTER, DEFAULT_FILTER, countsStrip,
  fmtCount, composerValue, CHAT_ERROR_WORDS, isAmbiguousSendError, AMBIGUOUS_SEND_TEXT, sendAttemptKey, threadListPollMs, reloadsOnCode,
  chatLogTagged,
} from '../lib/chatsView';
import { r16qUiOn, R16Q_TAG } from '../lib/r16qSwitch';

/*
 * r16p (2026-10-24), DESIGN D:/NecroWork/dm-chats-evidence/DESIGN.md 7.1: the Inbox at /inbox (alias /slots/:slotName/inbox).
 *
 * One page for the whole account: the conversation list on the left (cursor-paged, filtered on the SERVER by slot and filter - never
 * the whole account at once, design R27) and the open conversation on the right; on phones (below `sm`) two screens, the list and
 * then the conversation (?thread=), with "<- Chats" and the browser Back. URL state: ?slot=<slot NAME>&thread=<opaque conv id>&filter=
 * - never a handle in a URL.
 *
 * Not real time: messages reach the server when a slot's session reads its inbox, and a reply goes out from the phone at the slot's
 * next session (or that session's end pass). The page says so.
 *
 * The rules this page enforces on its side (the server enforces them all again):
 *   - wrong-thread guard (the assistant page's pendingForRef rule): a reply typed in conversation A is sent to A only - the draft is
 *     A's (lib/chatDraft.js, memory only), Send refuses when the conversation on screen is no longer the one the composer belongs to,
 *     and an answer that lands after a switch never touches the conversation now on screen;
 *   - one POST per press with a fresh clientRequestId and the basisSeq (the last message the page rendered: the HOLD basis) - except
 *     after an AMBIGUOUS failure (no answer, a timeout, a 5xx without a code of ours): then the next press with the same text in the
 *     same conversation re-sends that exact request (same id, same body), which the server answers from the stored row if the first
 *     one landed (design 6.6 / R9) - never a second reply to a real person (review P2);
 *   - no newlines (they become spaces, with a hint); duplicate_recent asks "Send it again?" (allowRepeat);
 *   - a reply that was not confirmed is NEVER re-sent on its own; "Queue again" asks first and names the double-send risk;
 *   - HELD replies wait for the author: "Send anyway" / "Confirm" / "Cancel";
 *   - third-party text is plain text; no text, name or handle in any console line ([R16P-UI] lines carry counts, codes, opaque ids);
 *   - mutations invalidate with { queryKey } only (an array alone matches every query in TanStack v5);
 *   - every query key carries the signed-in account (lib/chatsKeys.js, review T1): another account in the same tab never sees
 *     these rows, and components/ChatsCacheGuard.js drops them on sign-out.
 * Switch: lib/r14Switch.js r16pUiEnabled (OFF: the route goes to the dashboard).
 */

const PAGE_HEIGHT = cn(
  'min-h-[30rem]',
  'h-[calc(100dvh-var(--topbar-h)-var(--safe-top)-var(--bottombar-h)-var(--safe-bottom)-2.5rem)]',
  'sm:h-[calc(100dvh-var(--topbar-h)-var(--safe-top)-var(--bottombar-h)-var(--safe-bottom)-2.75rem)]',
  'lg:h-[calc(100dvh-var(--topbar-h)-var(--safe-top)-3.5rem)]',
);

const TONE_TEXT = { primary: 'text-primary', info: 'text-info', warning: 'text-warning', danger: 'text-danger', success: 'text-success' };

const EMPTY_TEXT = {
  all: 'No conversations yet. The worker reads a slot\'s inbox during its sessions once "Read my DM inbox (Chats)" is ticked in the slot\'s settings (section "DM Inbox (Chats)").',
  unread: 'No unread conversations.',
  needs_reply: 'Nothing needs a reply.',
  queued: 'No replies are queued.',
  held: 'No reply is waiting for you.',
  detached: 'No conversations have left the inbox.',
};

let loggedRouteMissing = false;

function useNow(ms = 60000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(t);
  }, [ms]);
  return now;
}

function FilterPicker({ value, onChange }) {
  const all = [...FILTERS, DETACHED_FILTER];
  return (
    <>
      <label className="sr-only" htmlFor="inbox-filter-select">Show</label>
      <select id="inbox-filter-select" value={value} onChange={(e) => onChange(e.target.value)} data-testid="inbox-filter-select"
        className="h-9 rounded-full border border-white/10 bg-secondary/60 px-3 text-sm text-foreground sm:hidden">
        {all.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
      </select>
      <div className="hidden flex-wrap items-center gap-1 rounded-full border border-white/10 bg-secondary/40 p-1 sm:flex" role="group" aria-label="Show">
        {all.map((f) => (
          <button key={f.value} type="button" onClick={() => onChange(f.value)} aria-pressed={value === f.value}
            className={cn('rounded-full px-3 py-1.5 text-xs font-medium transition-colors', value === f.value ? 'bg-primary/20 text-primary ring-1 ring-primary/30' : 'text-muted-foreground hover:text-foreground')}
            data-testid={`inbox-filter-${f.value}`}>
            {f.value === 'detached' ? 'Left the inbox' : f.label}
          </button>
        ))}
      </div>
    </>
  );
}

function CountsStrip({ summary, onFilter, className }) {
  const items = countsStrip(summary);
  return (
    <div className={cn('grid grid-cols-5 gap-1.5 sm:flex sm:flex-wrap sm:gap-2', className)} data-testid="inbox-counts" role="group" aria-label="Counts">
      {items.map((it) => {
        const Comp = it.filter ? 'button' : 'div';
        const v = it.value;
        return (
          <Comp key={it.key} type={it.filter ? 'button' : undefined} onClick={it.filter ? () => onFilter(it.filter) : undefined}
            className={cn('min-w-0 rounded-xl border border-white/[0.07] bg-card/60 px-2 py-1.5 text-left sm:inline-flex sm:items-baseline sm:gap-2 sm:px-3 sm:py-1',
              it.filter && 'transition-colors hover:border-primary/35')}
            data-testid={`inbox-count-${it.key}`}>
            <span className={cn('block text-base font-semibold leading-tight tnum', Number.isFinite(v) && v > 0 ? TONE_TEXT[it.tone] : 'text-foreground/80')}>{fmtCount(v)}</span>
            <span className="block text-[11px] font-medium leading-tight text-muted-foreground sm:truncate sm:uppercase sm:tracking-wide">{it.label}</span>
          </Comp>
        );
      })}
    </div>
  );
}

// r16pm review: the words of a 401 session_expired when the server sent none of its own
const SESSION_EXPIRED_TEXT = 'This session was signed out of the chat pages. Sign in again.';

export default function InboxPage() {
  usePageTitle('Inbox');
  const on = r16pUiEnabled();
  const auth = useOptionalAuth();
  const user = auth && auth.user;
  const owner = useChatsOwner();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const slot = slotParamFrom(params.get('slot'));
  // r16q10: several slots: ?slots=slot5-40,slot55. One slot stays ?slot=, and it wins when both come
  const slotsParam = slot ? null : slotsParamFrom(params.get('slots'));
  const picked = useMemo(() => (slot ? [slot] : decodeSlots(slotsParam)), [slot, slotsParam]);
  const threadId = threadParamFrom(params.get('thread'));
  const filter = filterFrom(params.get('filter'));
  const now = useNow();

  const setParam = useCallback((changes) => {
    const next = new URLSearchParams(params);
    Object.entries(changes).forEach(([k, v]) => { if (v === null || v === undefined || v === '') next.delete(k); else next.set(k, String(v)); });
    setParams(next);
  }, [params, setParams]);

  // ---------------------------------------------------------------- queries
  const infoQ = useQuery({
    queryKey: chatsKeys.info(owner),
    queryFn: () => chatsApi.info().then((r) => r.data),
    enabled: on,
    retry: false,
    staleTime: 300000,
    refetchInterval: 300000,
  });
  const info = useMemo(() => (infoQ.data ? normaliseInfo(infoQ.data) : null), [infoQ.data]);
  const infoErr = infoQ.error ? classifyChatsError(infoQ.error) : null;
  const ready = on && !!info && info.enabled;
  const chats = useChatsSummary({ enabled: on });

  const slotsQ = useQuery({ queryKey: ['slots'], queryFn: () => slotsApi.list().then((r) => r.data), enabled: ready, staleTime: 300000 });
  // r16q6 (Live mode R2): the phone's screen beside the chat, on by default; on a narrow screen Chat and Phone are two tabs
  const [showPhone, setShowPhone] = useState(getShowPhone);
  const [narrowTab, setNarrowTab] = useState('chat');
  const toggleShowPhone = useCallback(() => setShowPhone((on) => {
    rememberShowPhone(!on);
    return !on;
  }), []);

  // r16q7: Live mode. While a session is READY the open chat and the list are read often
  const [liveReady, setLiveReady] = useState(false);
  const listQ = useInfiniteQuery({
    queryKey: chatsKeys.threadList(owner, slot || slotsParam, filter),
    queryFn: ({ pageParam }) => chatsApi.threads({ slot, ...(slotsParam ? { slots: slotsParam } : {}), filter, cursor: pageParam || undefined }).then((r) => normaliseThreadList(r.data)),
    initialPageParam: null,
    getNextPageParam: (last) => (last && last.nextCursor) || undefined,
    enabled: ready,
    retry: false,
    // review: a refetch re-reads EVERY loaded page; the interval grows with them (lib/chatsView threadListPollMs: ~1 request a minute)
    // r16q10: during Live the list keeps up with what the phone shows; only the first pages, as every loaded page is read again each time
    refetchInterval: (q) => threadListPollMs(q && q.state ? q.state.data : null, liveReady),
    // the old rows stay (dimmed) while another filter loads - never another account's (review T1)
    placeholderData: keepPreviousForOwner(owner),
  });
  const listPages = useMemo(() => (listQ.data ? listQ.data.pages : []), [listQ.data]);
  const threads = useMemo(() => {
    const seen = new Set();
    const out = [];
    listPages.forEach((p) => p.threads.forEach((t) => { if (!seen.has(t.id)) { seen.add(t.id); out.push(t); } }));
    return out;
  }, [listPages]);
  const listSlot = listPages.length ? listPages[0].slot : null;
  const listErr = listQ.error ? classifyChatsError(listQ.error) : null;

  // NO placeholderData here: while conversation B loads, conversation A's messages must never stand in for it
  const detailQ = useQuery({
    queryKey: chatsKeys.thread(owner, threadId),
    queryFn: () => chatsApi.thread(threadId).then((r) => normaliseThreadDetail(r.data)),
    enabled: ready && !!threadId,
    retry: false,
    refetchInterval: (q) => (liveReady ? 2500 : threadPollMs(q.state.data)),
  });
  const detail = detailQ.data && detailQ.data.thread && detailQ.data.thread.id === threadId ? detailQ.data : null;
  // the list's slot context (mode, replyMode, syncEnabled) stands in only for a thread OF THAT SLOT (review: ?slot=A&thread=<conv of B>
  // must not gate B's composer and AI chips by A's mode)
  // the chat's own slot, else the slot picked at the top; no phone pane and no Live tick without one
  // r16q10: while the next chat loads, its row says whose it is; else the phone stays on the slot it was on, so its video doesn't start over
  const listedSlot = useMemo(() => {
    const row = threadId ? threads.find((t) => t.id === threadId) : null;
    return row ? row.slot : null;
  }, [threads, threadId]);
  const lastPhoneSlot = useRef(null);
  const phoneSlot = (detail && detail.thread && detail.thread.slot) || slot || listedSlot || (threadId ? lastPhoneSlot.current : null) || null;
  useEffect(() => { lastPhoneSlot.current = phoneSlot; }, [phoneSlot]);
  // r16q10: a chat opened on the phone during Live opens here too
  const followPhone = useRef(null);
  followPhone.current = (id) => setParam({ thread: id });
  const live = useChatLive(ready ? phoneSlot : null, threadId || undefined, followPhone);
  useEffect(() => { setLiveReady(live.ready); }, [live.ready]);

  // ---------------------------------------------------------------- r16q10: which slots
  const applyPick = useCallback((names) => {
    const next = pickParams(names);
    if (next.slots && next.slots.length > SLOTS_PARAM_MAX) {
      toast.error('Too many separate slots to show at once. Use a range, like 801-1200.');
      return;
    }
    const keep = threadId && (names.length === 0 || (detail && detail.thread && names.includes(detail.thread.slot)));
    setParam({ slot: next.slot, slots: next.slots, thread: keep ? threadId : null });
  }, [threadId, detail, setParam]);
  // a slot id on a chat: on its own, just that slot. With Ctrl (Cmd on a Mac) held, the ids build a list that applies when the key is let go, as the other slots' chats would vanish otherwise
  const [pendingSlots, setPendingSlots] = useState(null);
  const onSlotChip = useCallback((name, e) => {
    if (e.ctrlKey || e.metaKey) {
      setPendingSlots((cur) => togglePick(cur || picked, name));
      return;
    }
    setPendingSlots(null);
    applyPick([name]);
  }, [picked, applyPick]);
  useEffect(() => {
    if (!pendingSlots) return undefined;
    const done = (e) => {
      if (e.type === 'blur' || e.key === 'Control' || e.key === 'Meta') {
        setPendingSlots(null);
        applyPick(pendingSlots);
      }
    };
    window.addEventListener('keyup', done);
    window.addEventListener('blur', done);
    return () => {
      window.removeEventListener('keyup', done);
      window.removeEventListener('blur', done);
    };
  }, [pendingSlots, applyPick]);
  const detailWithSlot = useMemo(() => (
    detail && !detail.slot && listSlot && listSlot.name && detail.thread.slot && detail.thread.slot === listSlot.name
      ? { ...detail, slot: listSlot } : detail
  ), [detail, listSlot]);

  // ---------------------------------------------------------------- logs (counts, codes and opaque ids only)
  useEffect(() => {
    if (infoErr && infoErr.kind === 'routeMissing' && !loggedRouteMissing) {
      loggedRouteMissing = true;
      chatLog('info', 'inbox: no /uiapi/chats route on this server', { status: infoErr.status });
    }
  }, [infoErr]);
  const listCount = threads.length;
  useEffect(() => {
    if (listQ.data) chatLog('info', 'threads', { slot: slot || 'all', filter, n: listCount, more: !!listQ.hasNextPage });
  }, [listQ.data, listCount, slot, filter, listQ.hasNextPage]);
  useEffect(() => {
    if (detail) {
      chatLog('info', 'thread', {
        thread: detail.thread.id, msgs: detail.messages.length, replies: detail.replies.length,
        open: detail.replies.filter((r) => ['queued', 'claimed', 'sending', 'held'].includes(r.status)).length, mode: detail.slot ? detail.slot.mode : null,
        // r16pm review: whether the server has older messages than this page (its olderCursor, a seq number) - "Load older" shows then
        older: !!detail.olderCursor, lastSeq: detail.lastSeq,
      });
    }
  }, [detail]);

  // ---------------------------------------------------------------- the wrong-thread guard
  const threadRef = useRef(threadId);
  useEffect(() => { threadRef.current = threadId; }, [threadId]);
  const pendingForRef = useRef(null);
  const suggestionFor = useRef({}); // thread id -> the suggestion job whose text went into the composer

  // ---------------------------------------------------------------- older messages (a whole thread is never loaded at once)
  const [older, setOlder] = useState({ thread: null, messages: [], cursor: null, loading: false });
  useEffect(() => { setOlder({ thread: threadId, messages: [], cursor: null, loading: false }); }, [threadId]);
  const olderCursor = older.thread === threadId && older.cursor !== null ? older.cursor : (detail ? detail.olderCursor : null);
  const loadOlder = async () => {
    if (!threadId || !olderCursor || older.loading) return;
    const forThread = threadId;
    setOlder((o) => ({ ...o, loading: true }));
    try {
      const res = await chatsApi.thread(forThread, olderCursor);
      if (threadRef.current !== forThread) return;
      const d = normaliseThreadDetail(res.data);
      setOlder((o) => {
        const known = new Set([...o.messages, ...(detail ? detail.messages : [])].map((m) => m.id));
        // '' = the server has nothing older (its olderCursor null): the button goes. A cursor is always a string here (cursorOf).
        return { thread: forThread, messages: [...d.messages.filter((m) => !known.has(m.id)), ...o.messages], cursor: d.olderCursor !== null ? d.olderCursor : '', loading: false };
      });
      chatLog('info', 'older', { thread: forThread, n: d.messages.length, more: d.olderCursor !== null });
    } catch (e) {
      setOlder((o) => ({ ...o, loading: false }));
      toast.error(chatsErrorText(e, 'Older messages could not be loaded'));
    }
  };
  const olderMsgs = older.thread === threadId ? older.messages : [];
  const olderCursorShown = olderCursor === '' ? null : olderCursor;

  // ---------------------------------------------------------------- invalidation (always { queryKey })
  const refreshAll = useCallback((forThread) => {
    if (forThread) queryClient.invalidateQueries({ queryKey: chatsKeys.thread(owner, forThread) });
    queryClient.invalidateQueries({ queryKey: chatsKeys.threads(owner) });
    queryClient.invalidateQueries({ queryKey: chatsKeys.summary(owner) });
  }, [queryClient, owner]);

  // ---------------------------------------------------------------- the dashboard's own read marker (not Instagram's)
  const markedRef = useRef('');
  const markRead = useMutation({
    mutationFn: ({ id, seq }) => chatsApi.markRead(id, seq),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: chatsKeys.threads(owner) });
      queryClient.invalidateQueries({ queryKey: chatsKeys.summary(owner) });
    },
  });
  useEffect(() => {
    if (!detail) return;
    const t = detail.thread;
    const seq = detail.lastSeq;
    const behind = t.dashUnread || (seq !== null && (t.dashReadSeq === null || seq > t.dashReadSeq));
    const mark = `${t.id}:${seq}`;
    if (behind && markedRef.current !== mark) {
      markedRef.current = mark;
      markRead.mutate({ id: t.id, seq });
    }
  }, [detail]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------------------------------------------------------------- confirm dialog (one at a time)
  const [ask, setAsk] = useState(null); // { title, body, ok, tone, run, testid }

  // ---------------------------------------------------------------- send
  const [draft, setDraftRaw] = useChatDraft(owner, threadId);
  // a draft emptied by hand is no longer the AI suggestion it started from (origin AI_EDITED is only for text that came from a chip)
  const setDraft = (v) => { if (!v && threadId) delete suggestionFor.current[threadId]; setDraftRaw(v); };
  // review P2: the one request per conversation whose answer never came - { key: conversation + normalised text, body } - re-sent as
  // it is (same clientRequestId, same body) by the next press with the same text; dropped on any definitive answer
  const [unanswered, setUnanswered] = useState({});
  const unansweredRef = useRef(unanswered);
  unansweredRef.current = unanswered;
  const forgetAttempt = (forThread, key) => setUnanswered((m) => {
    if (!m[forThread] || (key && m[forThread].key !== key)) return m;
    const next = { ...m };
    delete next[forThread];
    return next;
  });
  const sendMut = useMutation({
    mutationFn: ({ forThread, body }) => chatsApi.queueReply(forThread, body),
    onSuccess: (res, vars) => {
      pendingForRef.current = null;
      forgetAttempt(vars.forThread, vars.key);
      const r = (res && res.data && (res.data.reply || res.data)) || {};
      const replayed = !!vars.replay && res && res.status === 200;
      setChatDraft(owner, vars.forThread, '');
      delete suggestionFor.current[vars.forThread];
      chatLog('info', 'reply queued', {
        // r16pm: the r16p server's reply view says `state`
        thread: vars.forThread, reply: typeof r.id === 'string' ? r.id : null,
        status: typeof (r.state || r.status) === 'string' ? String(r.state || r.status).toLowerCase() : null,
        len: vars.len, replay: !!vars.replay, http: res && typeof res.status === 'number' ? res.status : null,
      });
      refreshAll(vars.forThread);
      if (threadRef.current !== vars.forThread) toast.info('Your reply was queued in the conversation you left.');
      else if (replayed) toast.info('This reply was already queued by the send that got no answer. It was not queued twice.');
      else toast.success('Reply queued. The phone sends it at the slot\'s next session.');
    },
    onError: (e, vars) => {
      pendingForRef.current = null;
      const c = classifyChatsError(e);
      const ambiguous = isAmbiguousSendError(e);
      chatLog('warn', 'reply refused', { thread: vars.forThread, status: c.status, code: c.code, answer: ambiguous ? 'ambiguous' : 'definitive', replay: !!vars.replay });
      if (ambiguous) {
        // it may be stored: keep this exact request for a retry, say so, and re-read the conversation (a landed reply shows there)
        setUnanswered((m) => ({ ...m, [vars.forThread]: { key: vars.key, body: vars.body } }));
        toast.error(AMBIGUOUS_SEND_TEXT);
        refreshAll(vars.forThread);
        return;
      }
      forgetAttempt(vars.forThread, vars.key);
      if (c.code === 'duplicate_recent' && threadRef.current === vars.forThread && !vars.body.allowRepeat) {
        setAsk({
          title: 'Send the same text again?',
          body: 'You sent the same text in this thread recently. Send it again?',
          ok: 'Send it again',
          testid: 'confirm-repeat',
          // a deliberate new request: a fresh clientRequestId (the refused one stored nothing). Through the ref: this closure was made
          // while the first request was pending, and its own sendNow would still see it pending (review P1)
          run: () => sendNowRef.current(vars.forThread, { allowRepeat: true }),
        });
        return;
      }
      if (c.code === 'typing_too_long') {
        // r16pm: the r16p server sends its own message AND estMinutes (ChatReplyStore); both are shown
        const minutes = Number.isFinite(c.estMinutes) && c.estMinutes > 0 ? c.estMinutes : null;
        const base = (c.message || CHAT_ERROR_WORDS.typing_too_long).replace(/\.\s*$/, '');
        toast.error(`${base}${minutes ? ` (about ${minutes} minutes)` : ''}. Shorten it.`);
        return;
      }
      toast.error(chatsErrorText(e, 'The reply could not be queued'));
      // r16pm: the r16p server answers the eligibility reason itself (outbound_streak, conversation_detached, ...; ReplyRules), so
      // the conversation is read again and the closed composer shows why. r16q: also the merged server's request_not_ruled_out (the
      // conversation was re-read in Instagram's All tab / an unproven tab since this page read it; necro.ui.r16q.reloadNotRuledOut)
      if (c.code && reloadsOnCode(c.code, r16qUiOn('reloadNotRuledOut'))) {
        if (c.code === 'request_not_ruled_out') chatLogTagged(R16Q_TAG, 'info', 'send refused: the conversation is read again', { thread: vars.forThread, code: c.code, http: c.status });
        refreshAll(vars.forThread);
      }
    },
  });

  function sendNow(forThread, extra = {}) {
    // the guard: only the conversation on screen, only its own draft, only once at a time. "Once at a time" is the REF only: the
    // mutation's isPending seen from an older render's closure (the repeat dialog's) stays true after it settled (review P1)
    if (!forThread || forThread !== threadRef.current) {
      chatLog('warn', 'send refused: the conversation changed', { for: forThread, on: threadRef.current });
      return false;
    }
    if (pendingForRef.current) return false;
    if (!detail || detail.thread.id !== forThread) return false;
    const text = getChatDraft(owner, forThread).trim();
    if (!text) return false;
    const key = sendAttemptKey(forThread, text);
    const prior = unansweredRef.current[forThread];
    const replay = !!(prior && prior.key === key && !extra.allowRepeat);
    const body = replay ? prior.body : {
      text,
      clientRequestId: newClientRequestId(),
      basisSeq: basisSeqOf([...olderMsgs, ...detail.messages]),
      ...(suggestionFor.current[forThread] ? { suggestionId: suggestionFor.current[forThread] } : {}),
      ...extra,
    };
    if (replay) chatLog('info', 'reply re-sent as the same request', { thread: forThread });
    pendingForRef.current = forThread;
    sendMut.mutate({ forThread, body, key, replay, len: Array.from(text).length });
    return true;
  }
  const sendNowRef = useRef(sendNow);
  sendNowRef.current = sendNow;
  const draftKey = threadId && draft && draft.trim() ? sendAttemptKey(threadId, draft.trim()) : null;
  const sendNotice = threadId && unanswered[threadId] && unanswered[threadId].key === draftKey ? AMBIGUOUS_SEND_TEXT : null;

  const onPickSuggestion = (forThread, text, suggestionId) => {
    if (forThread !== threadRef.current) return;
    setChatDraft(owner, forThread, composerValue(text, !!(info && info.newlines)).text);
    if (suggestionId) suggestionFor.current[forThread] = String(suggestionId);
    chatLog('info', 'suggestion picked', { thread: forThread, job: suggestionId ? String(suggestionId) : null });
  };

  // ---------------------------------------------------------------- reply actions
  const [replyBusy, setReplyBusy] = useState(null);
  const replyAction = (kind, reply, fn, okText) => {
    const forThread = threadRef.current;
    setReplyBusy(reply.id);
    return fn(reply.id).then(() => {
      chatLog('info', `reply ${kind}`, { thread: forThread, reply: reply.id });
      if (okText) toast.success(okText);
    }).catch((e) => {
      const c = classifyChatsError(e);
      chatLog('warn', `reply ${kind} refused`, { thread: forThread, reply: reply.id, status: c.status, code: c.code });
      toast.error(chatsErrorText(e, `The reply could not be ${kind === 'cancel' ? 'cancelled' : kind === 'release' ? 'released' : 'queued again'}`));
    }).finally(() => {
      setReplyBusy(null);
      refreshAll(forThread);
    });
  };
  const onCancel = (reply) => replyAction('cancel', reply, chatsApi.cancelReply, 'Reply cancelled.');
  const onRelease = (reply) => replyAction('release', reply, chatsApi.releaseReply,
    reply.holdReason === 'confirm_peer' ? 'Confirmed. It is sent at the slot\'s next session.' : 'It is sent at the slot\'s next session.');
  const onRequeue = (reply) => setAsk({
    title: 'Queue this reply again?',
    body: reply.status === 'unconfirmed'
      ? 'We could not confirm the first one was sent. If it did go out, the other person may get the same message twice. Before typing, the worker first looks for this text in the conversation and does not send it again if it finds it.'
      : 'It was not sent. A new reply with the same text is queued for the slot\'s next session. Before typing, the worker first looks for this text in the conversation.',
    ok: 'Queue again',
    tone: 'warning',
    testid: 'confirm-requeue',
    run: () => replyAction('requeue', reply, chatsApi.requeueReply, 'Queued again.'),
  });

  // ---------------------------------------------------------------- thread actions
  const onToggleNeverOpen = (thread, value) => {
    chatsApi.setNeverOpen(thread.id, value).then(() => {
      chatLog('info', 'never-open', { thread: thread.id, on: !!value });
      toast.success(value ? 'The worker will not open this conversation on the phone.' : 'The worker may open this conversation again when it needs to.');
    }).catch((e) => toast.error(chatsErrorText(e, 'The change was not saved'))).finally(() => refreshAll(thread.id));
  };
  const onDelete = (thread) => setAsk({
    title: 'Delete this conversation?',
    body: 'Its stored messages and replies are deleted from NecroMancer\'s server, and the worker will not store this conversation again. Nothing changes on Instagram or on the phone.',
    ok: 'Delete',
    tone: 'danger',
    testid: 'confirm-delete',
    run: () => chatsApi.deleteThread(thread.id).then(() => {
      chatLog('info', 'thread deleted', { thread: thread.id });
      toast.success('Conversation deleted.');
      if (threadRef.current === thread.id) setParam({ thread: null });
      queryClient.removeQueries({ queryKey: chatsKeys.thread(owner, thread.id) });
      refreshAll(null);
    }).catch((e) => {
      const c = classifyChatsError(e);
      toast.error(c.status === 409 && !c.message ? CHAT_ERROR_WORDS.conversation_busy : chatsErrorText(e, 'The conversation was not deleted'));
    }),
  });

  // ---------------------------------------------------------------- the assistant bubble yields the corner while a composer is open
  const onComposerHeight = useCallback((h) => { if (h > 0) reserveFab('dm-composer', Math.round(h) + 8); }, []);
  useEffect(() => { if (!threadId) releaseFab('dm-composer'); }, [threadId]);
  useEffect(() => () => releaseFab('dm-composer'), []);

  /*
   * r16pm review (2026-10-25): 401 session_expired - this session was signed out of the chat pages ("Lock API access" in another
   * session, an admin password reset). A retry can never succeed and the nav has lost its chat items, so the way back is a REAL
   * sign-out (AuthContext.logout ends the server session too) and the sign-in page.
   */
  const [toLogin, setToLogin] = useState(false);
  const signInAgain = async () => {
    chatLog('info', 'session expired: sign in again', {});
    try {
      if (auth && typeof auth.logout === 'function') await auth.logout();
    } catch (e) { /* the local sign-out has happened; the sign-in page comes next either way */ }
    setToLogin(true);
  };

  if (!on) return <Navigate to="/dashboard" replace />;
  if (toLogin) return <Navigate to="/login" replace />;

  // ---------------------------------------------------------------- the page's honest states
  const header = (compactOnPhone) => (
    <PageHeader
      icon={Inbox}
      title="Inbox"
      description={live.running ? undefined : "Your slots' Instagram DMs, as the worker read them. Not real time: replies go out from the phone at the slot's next session."}
      className={cn('mb-3 shrink-0 sm:mb-4', compactOnPhone && threadId && 'hidden sm:block')}
      actions={ready ? (
        <>
          <SlotFilter slots={slotsQ.data} picked={picked} onPick={applyPick} />
          <FilterPicker value={filter} onChange={(v) => setParam({ filter: v === DEFAULT_FILTER ? null : v, thread: null })} />
          {/* r16pm: the account's retention and the slot-data erase (pages/ChatDataPage.js); the picked slot goes along */}
          <Link to={slot ? `/settings/chats?slot=${encodeURIComponent(slot)}` : '/settings/chats'} data-testid="inbox-chatdata-link"
            className="tap inline-flex h-9 items-center gap-1.5 rounded-full border border-white/10 bg-secondary/60 px-3 text-sm text-muted-foreground hover:text-foreground">
            <Database className="h-3.5 w-3.5" aria-hidden />Chat data
          </Link>
        </>
      ) : null}
    />
  );

  if (infoQ.isLoading) {
    return (
      <div className={cn('flex flex-col', PAGE_HEIGHT)} data-testid="inbox-page" data-state="loading">
        {header(false)}
        <div className="aurora-card flex-1 animate-pulse" aria-busy="true" aria-label="Loading the inbox" />
      </div>
    );
  }
  if (infoErr && infoErr.kind === 'routeMissing') {
    return (
      <div data-testid="inbox-page" data-state="route-missing">
        {header(false)}
        <div className="aurora-card aurora-card-dashed p-8 text-center text-sm text-muted-foreground" data-testid="inbox-no-route">
          This server has no inbox yet (it needs the r16p server). Nothing is shown rather than an empty inbox.
        </div>
      </div>
    );
  }
  if ((infoErr && infoErr.kind === 'notEnabled') || (info && !info.enabled)) {
    return (
      <div data-testid="inbox-page" data-state="off">
        {header(false)}
        <p className="flex items-start gap-2 rounded-xl border border-border bg-card/60 p-4 text-sm text-muted-foreground" data-testid="inbox-off">
          <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />{(infoErr && infoErr.message) || CHAT_ERROR_WORDS.not_enabled}
        </p>
      </div>
    );
  }
  if (infoErr && infoErr.kind === 'noAccess') {
    // the merged server's 403 no_chat_access (an Inventory client login): a plain statement, no "Try again" - it would only ask again
    return (
      <div data-testid="inbox-page" data-state="no-access">
        {header(false)}
        <p className="flex items-start gap-2 rounded-xl border border-border bg-card/60 p-4 text-sm text-muted-foreground" data-testid="inbox-no-access">
          <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />{infoErr.message || CHAT_ERROR_WORDS.no_chat_access}
        </p>
      </div>
    );
  }
  if (infoErr && infoErr.kind === 'sessionRequired') {
    return (
      <div data-testid="inbox-page" data-state="session-expired">
        {header(false)}
        <div className="aurora-card p-6 text-center" role="alert" data-testid="inbox-session-expired">
          <p className="text-sm text-muted-foreground">{infoErr.message || SESSION_EXPIRED_TEXT}</p>
          <Button variant="outline" size="sm" className="tap mt-4 rounded-full" onClick={signInAgain} data-testid="inbox-signin-again">Sign in again</Button>
        </div>
      </div>
    );
  }
  if (infoErr) {
    return (
      <div data-testid="inbox-page" data-state="error">
        {header(false)}
        <div className="aurora-card p-6 text-center" role="alert" data-testid="inbox-error">
          <p className="text-sm text-muted-foreground">{chatsErrorText(infoQ.error, 'The inbox could not be loaded')}</p>
          <Button variant="outline" size="sm" className="tap mt-4 rounded-full" onClick={() => infoQ.refetch()}><RefreshCw className="h-4 w-4" />Try again</Button>
        </div>
      </div>
    );
  }

  const requestsWaiting = (listSlot && listSlot.requestsUnread)
    || (!slot && chats.summary && Number.isFinite(chats.summary.requestsUnreadSlots) && chats.summary.requestsUnreadSlots > 0);
  const syncOff = !!(slot && listSlot && listSlot.syncEnabled === false);
  let emptyText = EMPTY_TEXT[filter] || EMPTY_TEXT.all;
  if (syncOff) emptyText = 'Reading this slot\'s inbox is off. Tick "Read my DM inbox (Chats)" in the slot\'s settings to let the worker read it during sessions.';
  else if (slot && filter === 'all') emptyText = 'No conversation of this slot has been read yet. The worker reads the inbox during the slot\'s sessions.';
  else if (picked.length > 1 && filter === 'all') emptyText = 'No conversation of these slots has been read yet. The worker reads the inbox during each slot\'s sessions.';

  let listBody;
  if (listErr && !listQ.data) {
    listBody = (
      <div className="px-4 py-8 text-center text-sm text-muted-foreground" data-testid="thread-list-error">
        <p>{listErr.kind === 'sessionRequired' ? (listErr.message || SESSION_EXPIRED_TEXT) : chatsErrorText(listQ.error, 'The conversations could not be loaded')}</p>
        {listErr.kind === 'sessionRequired'
          ? <Button variant="outline" size="sm" className="tap mt-3 rounded-full" onClick={signInAgain} data-testid="thread-list-signin-again">Sign in again</Button>
          : <Button variant="outline" size="sm" className="tap mt-3 rounded-full" onClick={() => listQ.refetch()}><RefreshCw className="h-4 w-4" />Try again</Button>}
      </div>
    );
  } else if (listQ.isLoading) {
    listBody = (
      <div className="space-y-2 p-3" aria-busy="true" aria-label="Loading conversations" data-testid="thread-list-loading">
        {[0, 1, 2, 3, 4].map((i) => <div key={i} className="h-14 animate-pulse rounded-xl bg-secondary/50" />)}
      </div>
    );
  } else {
    listBody = (
      <ThreadList threads={threads} selectedId={threadId} showSlot={!slot}
        // r16q10: the chat already open, clicked again during Live: the phone opens it again
        onOpen={(id) => { if (id === threadId && live.ready) live.input({ kind: 'reopen' }); else setParam({ thread: id }); }}
        onSlot={onSlotChip} pickedSlots={pendingSlots || picked}
        hasMore={!!listQ.hasNextPage} onMore={() => listQ.fetchNextPage()} loadingMore={listQ.isFetchingNextPage} now={now} emptyText={emptyText} />
    );
  }

  const detailError = detailQ.error ? chatsErrorText(detailQ.error, 'This conversation could not be loaded') : null;
  const phoneShown = showPhone && !!phoneSlot;
  const phoneTab = phoneShown && narrowTab === 'phone';

  return (
    <div className={cn('flex flex-col', PAGE_HEIGHT)} data-testid="inbox-page" data-state="ready">
      {header(true)}
      <div className={cn('mb-3 shrink-0 flex-wrap items-center gap-x-4 gap-y-2', threadId ? 'hidden sm:flex' : 'flex')}>
        <div className="order-last ml-auto flex min-w-0 flex-wrap items-center justify-end gap-2">
        <LiveToggle live={live} slot={phoneSlot} />
        <label className="tap inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-full border border-white/10 bg-secondary/60 px-3 text-xs text-muted-foreground hover:text-foreground" data-testid="inbox-show-phone">
          <input type="checkbox" className="h-3.5 w-3.5 accent-primary" checked={showPhone} onChange={toggleShowPhone} aria-label="Show phone" />
          <Smartphone className="h-3.5 w-3.5" aria-hidden />Show phone
        </label>
        </div>
        {/* r16q11: during Live these give their height to the phone's picture */}
        {!live.running && chats.summary && <CountsStrip summary={chats.summary} onFilter={(f) => setParam({ filter: f === DEFAULT_FILTER ? null : f, thread: null })} />}
        {!live.running && requestsWaiting && (
          <p className="flex min-w-0 max-w-xl items-start gap-2 text-xs text-muted-foreground" data-testid="inbox-requests-line">
            <MailWarning className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning" aria-hidden />
            Message requests are waiting on the phone. The worker never opens or accepts requests; accept one on the phone to reply here.
          </p>
        )}
        {syncOff && slot && (
          <p className="flex min-w-0 max-w-xl items-start gap-2 text-xs text-muted-foreground" data-testid="inbox-sync-off">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
            <span>Reading this slot&apos;s inbox is off. <Link className="font-medium text-primary hover:underline" to={`/slots/${encodeURIComponent(slot)}/settings`}>Open {slot}&apos;s settings</Link> (section &quot;DM Inbox (Chats)&quot;).</span>
          </p>
        )}
      </div>

      <div className="flex min-h-0 flex-1 gap-3 lg:gap-4">
        <section className={cn('aurora-card min-h-0 w-full flex-col overflow-hidden sm:w-72 sm:shrink-0 lg:w-80 xl:w-96', threadId ? 'hidden sm:flex' : 'flex',
          // r16q6: the phone needs the room on a mid-sized screen: the list steps aside while a chat is open
          threadId && phoneShown && 'sm:hidden xl:flex xl:w-72 2xl:w-96')}
          aria-label="Conversations" data-testid="inbox-list-pane">
          <div className={cn('flex min-h-0 flex-1 flex-col transition-opacity', listQ.isPlaceholderData && 'opacity-60')} aria-busy={listQ.isPlaceholderData || undefined}>
            {pendingSlots && (
              <p className="shrink-0 border-b border-border/60 bg-primary/10 px-3 py-1.5 text-xs text-primary" role="status" data-testid="inbox-slot-pending">
                {pendingSlots.length} {pendingSlots.length === 1 ? 'slot' : 'slots'} picked. Let go of Ctrl to show them.
              </p>
            )}
            {listBody}
          </div>
        </section>
        <section className={cn('aurora-card min-h-0 min-w-0 flex-1 flex-col overflow-hidden', threadId ? 'flex' : 'hidden sm:flex', phoneTab && 'hidden lg:flex')}
          aria-label="Conversation" data-testid="inbox-thread-pane">
          {threadId ? (
            <ThreadView
              detail={detailWithSlot}
              loading={detailQ.isLoading}
              error={detailError}
              onRetry={() => detailQ.refetch()}
              info={info}
              now={now}
              draft={draft}
              onDraft={(v) => { setDraft(v); live.activity(false); }}
              onSend={(forThread) => { live.activity(true); return sendNow(forThread); }}
              liveNow={live.ready}
              onOlderFromPhone={() => live.input({ kind: 'older' })}
              composerHint={live.ready ? 'Enter to send · Live: the phone types it within seconds' : live.running ? 'Enter to send · the phone sends it as soon as Live is ready' : null}
              sending={sendMut.isPending}
              sendNotice={sendNotice}
              onCancel={onCancel}
              onRelease={onRelease}
              onRequeue={onRequeue}
              replyBusy={replyBusy}
              older={olderMsgs}
              olderCursor={olderCursorShown}
              onLoadOlder={loadOlder}
              olderLoading={older.loading}
              onBack={() => setParam({ thread: null })}
              onToggleNeverOpen={onToggleNeverOpen}
              onDelete={onDelete}
              userId={user && user.id}
              onComposerHeight={onComposerHeight}
              onPickSuggestion={onPickSuggestion}
            />
          ) : <NoThreadPicked />}
        </section>
        {phoneShown && (threadId || slot) && (
          <PhonePane slotName={phoneSlot} slots={slotsQ.data} live={live}
            className={cn('aurora-card min-h-0 w-full shrink-0 flex-col overflow-hidden lg:flex lg:w-80 2xl:w-[26rem]', phoneTab ? 'flex' : 'hidden')} />
        )}
      </div>
      {phoneShown && threadId && (
        <div className="mt-2 flex shrink-0 flex-wrap items-center justify-center gap-1 lg:hidden" role="tablist" aria-label="Chat or phone" data-testid="inbox-narrow-tabs">
          <LiveToggle live={live} slot={phoneSlot} className="sm:hidden" />
          {[['chat', 'Chat'], ['phone', 'Phone']].map(([v, label]) => (
            <button key={v} type="button" role="tab" aria-selected={narrowTab === v} onClick={() => setNarrowTab(v)} data-testid={`inbox-tab-${v}`}
              className={cn('tap rounded-full border border-white/10 px-4 py-1.5 text-sm', narrowTab === v ? 'bg-primary/20 text-foreground' : 'bg-secondary/60 text-muted-foreground')}>
              {label}
            </button>
          ))}
        </div>
      )}

      <AlertDialog open={!!ask} onOpenChange={(o) => { if (!o) setAsk(null); }}>
        <AlertDialogContent data-testid={ask ? ask.testid : 'confirm'}>
          <AlertDialogHeader>
            <AlertDialogTitle>{ask ? ask.title : ''}</AlertDialogTitle>
            <AlertDialogDescription data-testid="confirm-text">{ask ? ask.body : ''}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-ok"
              className={ask && ask.tone === 'danger' ? 'bg-danger text-white hover:bg-danger/90' : (ask && ask.tone === 'warning' ? 'bg-warning text-background hover:bg-warning/90' : '')}
              onClick={() => { const run = ask && ask.run; setAsk(null); if (run) run(); }}>
              {ask ? ask.ok : 'OK'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
